import DocumentEditorPanel from "Areas/Documents/documentEditorPanel";
import { APP_SIZES } from "Common/componentUtils/constants";
import { SORT_ORDER } from "Common/dictionaries/sortOrder";
import compact from "lodash/compact";
import head from "lodash/head";
import omit from "lodash/omit";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { SidebarMenuToggler, SidebarSearchPanel, Styled } from "../Common";
import { TAB } from "./constants";
import locale from "./documentsSidebarMenu.locale";
import ScriptEditorPanel from "./scriptEditorPanel";
import SidebarMenuTabs from "./sidebarMenuTabs";
import TabDocuments from "./tabDocuments";
import TabMethods from "./tabMethods";
import TabNavigationSwitcher from "./tabNavigationSwitcher";
import TabObjects from "./tabObjects";
import TabScripts from "./tabScripts";
import TabTasks from "./tabTasks";
import styles from "./documentsSidebarMenu.module.css";
const tabsLocale = {
    [TAB.DOCUMENTS]: locale.DOCUMENTS,
    [TAB.NAVIGATION]: locale.NAVIGATION,
    [TAB.OBJECTS]: locale.OBJECTS,
    [TAB.METHODS]: locale.METHODS,
    [TAB.SCRIPTS]: locale.SCRIPTS,
    [TAB.TASKS]: locale.TASKS
};
const baseTabs = {
    [TAB.DOCUMENTS]: {
        key: TAB.DOCUMENTS,
        title: tabsLocale[TAB.DOCUMENTS],
        icon: "icon-folder"
    },
    [TAB.NAVIGATION]: {
        key: TAB.NAVIGATION,
        title: tabsLocale[TAB.NAVIGATION],
        icon: "icon-nav"
    },
    [TAB.OBJECTS]: {
        key: TAB.OBJECTS,
        title: tabsLocale[TAB.OBJECTS],
        icon: "icon-object"
    },
    [TAB.METHODS]: {
        key: TAB.METHODS,
        title: tabsLocale[TAB.METHODS],
        icon: "icon-method"
    },
    [TAB.SCRIPTS]: {
        key: TAB.SCRIPTS,
        title: tabsLocale[TAB.SCRIPTS],
        icon: "icon-script"
    },
    [TAB.TASKS]: {
        key: TAB.TASKS,
        title: tabsLocale[TAB.TASKS],
        icon: "icon-tasks"
    }
};
const tabsComponent = {
    [TAB.DOCUMENTS]: TabDocuments,
    [TAB.OBJECTS]: TabObjects,
    [TAB.NAVIGATION]: TabNavigationSwitcher,
    [TAB.TASKS]: TabTasks,
    [TAB.SCRIPTS]: TabScripts,
    [TAB.METHODS]: TabMethods
};
const availableSortBy = {
    [TAB.DOCUMENTS]: [
        { Key: "sourceData.Created", Name: locale.SORT_BY_CREATION },
        { Key: "title", Name: locale.SORT_BY_TITLE }
    ],
    [TAB.OBJECTS]: [{ Key: "title", Name: locale.SORT_BY_TITLE }],
    [TAB.NAVIGATION]: [{ Key: "title", Name: locale.SORT_BY_TITLE }],
    [TAB.TASKS]: [
        { Key: "Date", Name: locale.SORT_BY_CREATION },
        { Key: "Name", Name: locale.SORT_BY_TITLE }
    ],
    [TAB.METHODS]: [{ Key: "title", Name: locale.SORT_BY_TITLE }],
    [TAB.SCRIPTS]: [
        { Key: "sourceData.Created", Name: locale.SORT_BY_CREATION },
        { Key: "title", Name: locale.SORT_BY_TITLE }
    ]
};
const sortOrderDefault = {
    [TAB.DOCUMENTS]: SORT_ORDER.DESC,
    [TAB.TASKS]: SORT_ORDER.DESC,
    [TAB.SCRIPTS]: SORT_ORDER.DESC
};
export default function DocumentsSidebarMenu(props) {
    const [filterText, setFilterText] = useState("");
    const [sortBy, setSortBy] = useState();
    const [sortOrder, setSortOrder] = useState(SORT_ORDER.ASC);
    const [Component, setComponent] = useState();
    const tabs = useMemo(() => omit(baseTabs, compact([
        !props.canViewMethods && TAB.METHODS,
        !props.canViewScripts && TAB.SCRIPTS,
        !props.canViewTasks && TAB.TASKS
    ])), []);
    useLayoutEffect(() => {
        var _a;
        setFilterText("");
        setSortBy((_a = head(availableSortBy[props.tabKey])) === null || _a === void 0 ? void 0 : _a.Key);
        setSortOrder(sortOrderDefault[props.tabKey] || SORT_ORDER.ASC);
        setComponent(tabsComponent[props.tabKey]);
    }, [props.tabKey]);
    useEffect(() => {
        props.documentType &&
            props.onDocumentTypeChanged &&
            props.onDocumentTypeChanged(props.documentType);
    }, [props.documentType]);
    const handleToggleSortOrder = useCallback(() => {
        setSortOrder(prev => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
    }, []);
    const handleChangeSortBy = useCallback((value) => {
        setSortBy(value);
    }, []);
    const searchPanel = useMemo(() => (React.createElement(Styled.LogoContainer, { className: "unselectable", sidebarWidth: props.width ? props.width - APP_SIZES.SIDEBAR_COLLAPSED_WIDTH : 0 },
        React.createElement("h6", { className: styles.title }, tabsLocale[props.tabKey]),
        React.createElement(SidebarSearchPanel, { searchClassName: "darker-texteditor", filterText: filterText, onFilterTextChange: setFilterText, availableSortBy: availableSortBy[props.tabKey], sortBy: sortBy, sortOrder: sortOrder, onToggleSortOrder: handleToggleSortOrder, onChangeSortBy: handleChangeSortBy }))), [
        props.width,
        props.tabKey,
        filterText,
        sortBy,
        sortOrder,
        handleToggleSortOrder,
        handleChangeSortBy
    ]);
    return (React.createElement(Styled.SidebarMenuContainer, { width: props.width, className: props.className, bgColorDefault: "navbar-bg" },
        React.createElement(SidebarMenuTabs, { defaultHash: props.defaultHash, productName: props.productName, rows: props.sidebarMenuTabsRows || 1, onExpandedChanged: props.onExpandedChanged, expandedMenu: props.isExpanded, tabs: tabs }),
        searchPanel,
        Component ? (React.createElement(Component, { filterText: filterText, sortBy: sortBy, sortOrder: sortOrder, bgColorDefault: "navbar-bg-item-default", bgColorActive: "navbar-bg-item-active" })) : (React.createElement("div", null)),
        React.createElement(DocumentEditorPanel, { activeNewDocumentAfterCreation: true }),
        React.createElement(ScriptEditorPanel, null),
        React.createElement(SidebarMenuToggler, { isExpanded: props.isExpanded, onExpandedChanged: props.onExpandedChanged })));
}
